import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen } from "lucide-react";
import api from "../../lib/api";

/**
 * RelatedBlogPosts — display-only cross-linking used on service landing pages.
 *
 * Fetches the public blog feed (no admin writes, no mutation of DB) and ranks
 * posts by relevance to the current service page using a light heuristic:
 *   +5  post.related_expertise contains `expertiseSlug`
 *   +3  post.tags contains any of `keywords`
 *   +2  post.category matches a keyword
 *   +1  post.title contains a keyword (word-boundary)
 * The top `limit` posts (score > 0) are rendered; the block renders nothing
 * if no posts match — safe fallback for a fresh install.
 *
 * Props:
 *   expertiseSlug  string  Optional canonical expertise slug (highest signal).
 *   keywords       string[] Lowercase keywords used for matching.
 *   title          string   Section title (default: "From our blog").
 *   overline       string   Small overline label (default: "Related reading").
 *   limit          number   Max posts to render (default 3).
 */
const RelatedBlogPosts = ({ expertiseSlug, keywords = [], title, overline, limit = 3 }) => {
  const [posts, setPosts] = useState([]);
  const kws = useMemo(
    () => (keywords || []).map((k) => (k || "").toLowerCase()).filter(Boolean),
    [keywords]
  );

  useEffect(() => {
    api
      .get("/blog")
      .then((r) => setPosts(Array.isArray(r.data) ? r.data : []))
      .catch(() => setPosts([]));
  }, []);

  const matches = useMemo(() => {
    if (!posts.length) return [];
    const score = (p) => {
      let s = 0;
      const relExp = Array.isArray(p.related_expertise) ? p.related_expertise : [];
      if (expertiseSlug && relExp.includes(expertiseSlug)) s += 5;
      const tags = (p.tags || []).map((t) => (t || "").toLowerCase());
      if (kws.some((k) => tags.includes(k))) s += 3;
      const cat = (p.category || "").toLowerCase();
      if (kws.some((k) => cat.includes(k))) s += 2;
      const title = (p.title || "").toLowerCase();
      if (kws.some((k) => k && new RegExp(`\\b${k.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")}\\b`).test(title))) s += 1;
      return s;
    };
    return posts
      .map((p) => ({ p, s: score(p) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, limit)
      .map((x) => x.p);
  }, [posts, expertiseSlug, kws, limit]);

  if (matches.length === 0) return null;

  return (
    <section className="py-10 container-page" data-testid="related-blog-posts">
      <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] mb-3">
        {overline || "Related reading"}
      </div>
      <h2 className="font-serif text-3xl">{title || "From our blog"}</h2>
      <div className="h-[2px] w-12 bg-[hsl(var(--accent))] mt-3 mb-6" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {matches.map((p) => (
          <Link
            key={p.slug}
            to={`/blog/${p.slug}`}
            data-testid={`related-blog-${p.slug}`}
            className="group border border-border rounded-xl p-5 hover:border-[hsl(var(--primary))] transition-colors flex flex-col"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] inline-flex items-center gap-1.5">
                <BookOpen size={11} /> {p.category || "Blog"}
              </div>
              <ArrowUpRight
                size={16}
                strokeWidth={1.5}
                className="text-[hsl(var(--accent))] shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
              />
            </div>
            <div className="font-serif text-lg mt-3 leading-snug group-hover:text-[hsl(var(--primary))]">
              {p.title}
            </div>
            {p.excerpt && (
              <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{p.excerpt}</p>
            )}
          </Link>
        ))}
      </div>
    </section>
  );
};

export default RelatedBlogPosts;
